import { useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useWabaStore } from '@/stores/wabaStore'
import { wabaApi } from '@/api/endpoints/waba'
import { queryKeys } from '@/api/query-keys'

export function useWaba() {
  const queryClient = useQueryClient()
  const { selectedWabaId, wabaList, hasWaba, selectWaba: storeSelectWaba } = useWabaStore()

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.wabaList,
    queryFn: wabaApi.getWabaList,
    staleTime: 5 * 60 * 1000,
    // Re-check on mount so a reload/navigation during onboarding never shows
    // stale `has_waba: false` after the backend sync has finished.
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    // Self-poll while the tenant has no WABA yet (onboarding in progress) and
    // stop the moment one exists. This is declarative and survives re-renders,
    // remounts and reloads — unlike a manual setInterval whose lifecycle can be
    // clobbered by an effect cleanup.
    refetchInterval: (query) => (query.state.data?.has_waba ? false : 3000),
  })

  // Sync query data to store via useEffect (not inline during render)
  useEffect(() => {
    if (data && data.data) {
      const store = useWabaStore.getState()
      if (store.wabaList.length !== data.data.length || store.hasWaba !== data.has_waba) {
        store.setWabaData(
          data.data,
          data.default_waba_id != null ? String(data.default_waba_id) : null,
          data.has_waba,
        )
      }
    }
  }, [data])

  const switchWaba = useCallback(
    (wabaId: string) => {
      storeSelectWaba(wabaId)
      // Invalidate only WABA-scoped queries, not all queries
      queryClient.invalidateQueries({ queryKey: queryKeys.wabaList })
      queryClient.invalidateQueries({ predicate: (query) => {
        const key = query.queryKey
        // Invalidate queries that contain the old or new waba ID in their key
        return Array.isArray(key) && key.some((k) => typeof k === 'string' && (k === selectedWabaId || k === wabaId))
      }})
    },
    [storeSelectWaba, queryClient, selectedWabaId],
  )

  const selectedWaba = wabaList.find((w) => String(w.id) === selectedWabaId)

  return {
    wabaList,
    selectedWabaId,
    selectedWaba,
    hasWaba,
    isLoading,
    switchWaba,
  }
}
